import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";
import { useNavigate } from "react-router-dom";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  KeyRound,
  RefreshCw,
  RotateCcw,
  UserPlus,
} from "lucide-react";
import { preventSpaceInput, removeSpacesFromInput } from "util/preventSpecialCharInput";
import { emailSchema } from "util/validation";

// ─── Validation Schema ────────────────────────────────────────────────────────

const changePasswordSchema = yup.object().shape({
  // CHANGE: yup's built-in .email() is weaker than the app's own rule
  // (it accepts user@localhost); routed through the shared schema.
  email: emailSchema({
    requiredMessage: "Email is required",
    invalidMessage: "Enter a valid email address",
  }),
  newPassword: yup
    .string()
    .min(8, "Password must be between 8 and 16 characters")
    .max(16, "Password must be between 8 and 16 characters")
    .matches(
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9\s])[^\s]+$/,
      "Password must contain at least one digit, one lowercase letter, one uppercase letter, one special character, and no whitespace"
    )
    .required("New password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Passwords do not match")
    .max(16, "Confirm password must not exceed 16 characters")
    .required("Please confirm your new password"),
});

// ─── Password Strength Helper ─────────────────────────────────────────────────

function getPasswordStrength(value: string): {
  score: number;
  label: string;
  color: string;
} {
  if (!value) return { score: 0, label: "", color: "" };
  let score = 0;
  if (value.length >= 6) score++;
  if (value.length >= 10) score++;
  if (/[A-Z]/.test(value)) score++;
  if (/[0-9]/.test(value)) score++;
  if (/[^A-Za-z0-9]/.test(value)) score++;

  if (score <= 1) return { score, label: "Weak", color: "bg-red-400" };
  if (score <= 3) return { score, label: "Fair", color: "bg-yellow-400" };
  return { score, label: "Strong", color: "bg-green-500" };
}

// ─── Field Component ──────────────────────────────────────────────────────────

type FieldProps = {
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
};

const Field = ({ label, error, required, children }: FieldProps) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {error && (
      <p className="flex items-center gap-1 text-xs text-red-500 mt-0.5">
        <span className="inline-block w-1 h-1 rounded-full bg-red-500 flex-shrink-0" />
        {error}
      </p>
    )}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const ChangePassword = () => {
  const { addToast } = useToaster();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const navigate = useNavigate();

  const methods = useForm({
    resolver: yupResolver(changePasswordSchema),
    mode: "onChange",
    // 1. Explicitly set default values to empty strings
    defaultValues: {
      email: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const {
    register,
    watch,
    setError,
    clearErrors,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = methods;

  const passwordValue = watch("newPassword", "");
  const strength = getPasswordStrength(passwordValue);
  const confirmPasswordValue = watch("confirmPassword");

  // Validate password match dynamically
  useEffect(() => {
    if (passwordValue && confirmPasswordValue && passwordValue !== confirmPasswordValue) {
      setError("confirmPassword", { type: "manual", message: "Passwords do not match" });
    } else {
      clearErrors("confirmPassword");
    }
  }, [passwordValue, confirmPasswordValue, setError, clearErrors]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    const payload = {
      ...data,
      email : data.email.trim().toLowerCase()
    }
    try {
      await apiService.put("api/admin/change-password", payload);
      addToast("Password changed successfully", "success");
      reset();
    } catch (error) {
      addToast(error.response.data.message || "Failed to change password. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputBase =
    "w-full pl-10 pr-4 h-10 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all";
  const inputNormal = "border-gray-200 dark:border-gray-600";
  const inputError = "border-red-400 focus:ring-red-400";
  
  const primaryBtnClass =
    "flex-1 flex items-center justify-center gap-2 h-10 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors shadow-sm";
  const secondaryBtnClass =
    "flex items-center justify-center gap-2 h-10 px-3 md:px-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors";

  return (
    <FormProvider {...methods}>
      <div className="min-h-[calc(100vh-2px)] flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-800">
        <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                <KeyRound className="text-white w-5 h-5" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white leading-tight">Change Password</h1>
                <p className="text-blue-100 text-xs">Update user password securely</p>
              </div>
            </div>
          </div>

          {/* Form body */}
          {/* 2. Added autoComplete="off" to the form element */}
          <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-5 space-y-4" autoComplete="off">
            
            {/* Email */}
            <Field label="Email Address" error={errors.email?.message as string} required>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Mail size={15} />
                </div>
                <input
                  {...register("email")}
                   onKeyDown={preventSpaceInput}
                   onInput={removeSpacesFromInput}
                  type="email"
                  placeholder="Enter email address"
                  autoComplete="off" // Prevent browser from guessing it's a login email
                  className={`${inputBase} ${errors.email ? inputError : inputNormal}`}
                />
              </div>
            </Field>

            {/* New Password */}
            <Field label="New Password" error={errors.newPassword?.message as string} required>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Lock size={15} />
                </div>
                <input
                  {...register("newPassword")}
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  autoComplete="new-password" // Crucial: Tells password managers this is a NEW password, not a login
                  className={`${inputBase} pr-10 ${errors.newPassword ? inputError : inputNormal}`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              
              {/* Password strength bar */}
              {passwordValue && (
                <div className="mt-1.5 space-y-1">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-all ${
                          i <= strength.score ? strength.color : "bg-gray-200 dark:bg-gray-600"
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-[11px] font-medium ${
                    strength.score <= 1 ? "text-red-500" :
                    strength.score <= 3 ? "text-yellow-500" : "text-green-600"
                  }`}>
                    {strength.label} password
                  </p>
                </div>
              )}
            </Field>

            {/* Confirm New Password */}
            <Field label="Confirm New Password" error={errors.confirmPassword?.message as string} required>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Lock size={15} />
                </div>
                <input
                  {...register("confirmPassword")}
                  type={showConfirm ? "text" : "password"}
                  placeholder="Re-enter new password"
                  autoComplete="new-password" // Stop autofill here too
                  className={`${inputBase} pr-10 ${errors.confirmPassword ? inputError : inputNormal}`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                >
                  {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </Field>

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <button
                type="submit"
                disabled={isSubmitting || !isDirty}
                className={primaryBtnClass}
              >
                <RefreshCw size={15} />
                {isSubmitting ? "Submitting…" : "Change Password"}
              </button>
              <button
                type="button"
                onClick={() => reset()}
                className={secondaryBtnClass}
              >
                <RotateCcw size={14} />
                Reset
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/register')}
                className={secondaryBtnClass}
              >
                <UserPlus size={14} />
                Register
              </button>
            </div>
          </form>

        </div>
      </div>
    </FormProvider>
  );
};

export default ChangePassword;